import * as React from 'react';
import './_CustomProperty.scss';
import { ICustomPropertiesElement, ICustomPropertyProps } from './types';
import { CLASS_CUSTOM_PROPERTY_ITEM } from '../constants';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { ECustomPropertyType } from '../../../../../../../store/customProperties/types';
import { Button, Icon, Input, ISelectActiveInheritedProps, Select, Tooltip } from 'kui';
import { escape } from 'underscore';
import { validateEmail } from '../../../../../helpers/emailHelper';
import {
    CUSTOM_BUTTON_SAVE,
    CUSTOM_ERROR_EMAIL,
    CUSTOM_ERROR_PHONE,
    CUSTOM_LABEL_MAP,
    CUSTOM_PROPERTY_SELECT_DEFAULT_VALUE
} from './constants';
import { validatePhone } from '../../../../../helpers/phoneHelper';
import { DatepickerHOC } from '../../../../../base/components/Datepicker/hocs/DatepickerHOC/DatepickerHOC';
import { CustomPropertyActionsHOC } from '../../hocs/CustomPropertyActionsHOC/CustomPropertyActionsHOC';
import {
    CustomPropertiesPushToGoogleCalendarHOC
} from '../../hocs/CustomPropertiesPushToGoogleCalendarHOC/CustomPropertiesPushToGoogleCalendarHOC';
import { CustomPropertyTypeSelect } from '../CustomPropertyTypeSelect/CustomPropertyTypeSelect';
import {
    ID_ASIDE_PANEL_DATEPICKER_PORTAL
} from '../../../../../aside_panel/asidePanel/components/AsidePanelActionsButton/constants';
import { getAsidePanelTooltip } from '../../../../../aside_panel/asidePanel/helpers/getAsidePanelTooltip';
import { CARD_CONTROL_CLASS } from '../../../../../main/kanbanView';
import { ErrorHint } from '../../../ErrorHint/ErrorHint';
import ReactHtmlParser from 'react-html-parser';
import { blockMarkdown } from '../../../../../../../helper/markdownHelper';
import { onImgLoad } from '../../../../../main/kanbanView/components/helpers/onImgLoad';

//@ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CustomProperty`) : null;

export function CustomProperty ({
    cardId,
    customProperty,
    isHideCloseButton,
    isDatePushed,
    isPopup,
    isReadonly,
    onBlur,
    onCancel,
    onChange,
    onEdit,
    onFocus: onFocusOwn,
    onShowBoardLimitSnackbarIfNeeded,
    onHeightChange,
    onToggleLoadingImg,
    onUpdateValue,
    setCardHeight,
}: ICustomPropertyProps) {
    if (analyzeRender) analyzeRender.call(`CustomProperty ${cardId}`);

    const isKanbanCard = !!setCardHeight;

    const {
        id,
        type,
        name,
        isChanged,
        options,
        isRequired,
        isInputError,
        error,
        value,
        removed,
    } = customProperty;

    const className = CLASS_CUSTOM_PROPERTY_ITEM;
    const classInput = className + '-input';
    const classSave = className + '-save';
    const classWithSave = className + '-with-save';

    const [isError, setError] = React.useState(null);
    const [telCursor, setTelCursor] = React.useState(null);
    const [isFocus, setFocus] = React.useState(null);
    const [isEdit, setEdit] = React.useState(null);
    const [inputValue, setInputValue] = React.useState(null);

    const isShowCloseButton = (isFocus || isEdit) && !isHideCloseButton && type !== ECustomPropertyType.DATE && type !== ECustomPropertyType.SELECT;

    function castValue (value: string) {
        return value === undefined || value === '' ? null : value;
    }

    const setCardHeightIfNeeded = () => {
        if (!isKanbanCard) return;
        setCardHeight();
    }

    function errorEmail (
        property: ICustomPropertiesElement,
        value?: string,
    ): string {
        const { value: currentValue = '', id } = property;
        let isValue = value !== undefined;
        if (!isValue) {
            value = currentValue;
        }
        if (castValue(value) === null || validateEmail(value)) return null;
        const error = CUSTOM_ERROR_EMAIL;
        if (!isValue) {
            onChange({
                ...property,
                error,
            });
        }
        return error;
    }

    function errorPhone (
        property: ICustomPropertiesElement,
        value?: string,
    ): string {
        const { value: currentValue = '', id } = property;
        let isValue = value !== undefined;
        if (!isValue) {
            // mac adds invisible symbols on copy from contacts KNB-2606
            value = currentValue.replace(/[^ -~]+/g, '');
        }
        if (castValue(value) === null || validatePhone(value)) return null;
        const error = CUSTOM_ERROR_PHONE;
        if (!isValue) {
            onChange({
                ...property,
                error,
            });
        }
        return error;
    }

    function onSave () {
        const { error, type, value, id} = customProperty;
        if (error) return;

        if (type === ECustomPropertyType.EMAIL) {
            if (errorEmail(customProperty)) return;
        } else if (type === ECustomPropertyType.PHONE) {
            if (errorPhone(customProperty)) return;
        }
        setEdit(false);
        setFocus(false);
        onUpdateValue(id, castValue(value));
        onChange({
            ...customProperty,
            isChanged: false,
        });
    }

    const onChangeSelect = (event: ISelectActiveInheritedProps) => {
        onChange({ ...customProperty, value: event.item.value, });
        onUpdateValue(id, event.item.value === CUSTOM_PROPERTY_SELECT_DEFAULT_VALUE ? null : event.item.value);
    };

    const onBlurInput = () => {
        setCardHeightIfNeeded();
        setEdit(false);
        if (onBlur) onBlur();

        if (type !== ECustomPropertyType.DATE && type !== ECustomPropertyType.SELECT) setFocus(false);
        onSave();
        if (isRequired && !value) {
            if (type === ECustomPropertyType.SELECT) {
                setError(true);
            } else {
                onChange({ ...customProperty, isInputError: true });
            }
        }
    };

    const onKeyDown = (
        e: React.KeyboardEvent | KeyboardEvent,
    ) => {
        if (!e) return;

        if (e.key === 'Escape') {
            onCancelEdit();
            e.stopPropagation(); //to avoid aside-panel closure on esc if description is in focus
        }

        if (e.key === 'Enter') {
            onSave();
        }
    };

    const onCancelEdit = () => {
        if (onCancel) onCancel();
        setEdit(false);
        setFocus(false);
    }

    const onChangeInput = (
        event: React.FormEvent,
    ) => {
        setCardHeightIfNeeded();

        const input = event.target as HTMLInputElement;
        let { value = null, selectionStart } = input;

        let newError = error;

        if (newError) {
            if (type === ECustomPropertyType.EMAIL) {
                newError = errorEmail(customProperty, value);
            } else if (type === ECustomPropertyType.PHONE) {
                setTelCursor(selectionStart);
                // mac adds invisible symbols on copy from contacts KNB-2606
                value = value.replace(/[^ -~]+/g, '');
                newError = errorPhone(customProperty, value);
            }
        }

        onChange({ ...customProperty, error: newError, value, isChanged: true });
    };

    const onFocus = () => {
        if (onFocusOwn) onFocusOwn();
        if (type !== ECustomPropertyType.DATE && type !== ECustomPropertyType.SELECT) setFocus(true);
        if (!isRequired) return;
        if (type === ECustomPropertyType.SELECT) {
            setError(false);
        } else {
            onChange({ ...customProperty, isInputError: false });
        }

        if (type === ECustomPropertyType.PHONE) {
            const input = document.querySelector(`.${className}--${id}`) as HTMLInputElement;
            if (input) input.setSelectionRange(telCursor, telCursor);
        }
    };

    const onChangeDate = (date: Date) => {
        const value = date ? String(date.getTime()) : null;
        onChange({ ...customProperty, value });
        onUpdateValue(id, value);
    };

    const onStartEdit = () => {
        if (isReadonly) return;
        setEdit(true);
        setFocus(true);
    };

    const onClickHtml = (e: React.MouseEvent) => {
        const { tagName } = e.target as HTMLElement;
        if (tagName && tagName.toLowerCase() === 'a') {
            return;
        }

        onStartEdit();
    };

    const updateInputValue = (value: string) => {
        setInputValue(value)
    }

    const ref = React.useRef(null);
    const [markdown, setMarkdown] = React.useState('');
    React.useEffect(() => {
        setMarkdown(blockMarkdown(value));
    }, [value]);

    React.useEffect(() => {
        if (!onHeightChange || !markdown || !ref.current) return;
        const cleanup = onImgLoad(ref.current, onHeightChange, onToggleLoadingImg);
        return cleanup;
    }, [markdown]);

    let input;

    if (type === ECustomPropertyType.TEXT) {
        input = <Input
            className={isChanged ? classWithSave : ''}
            value={castValue(value) !== null ? String(value) : ''}
            state={isInputError ? 'error' : null}
            onBlur={onBlurInput}
            onFocus={onFocus}
            onChange={onChangeInput}
            onKeyDown={onKeyDown}
            onClick={setCardHeightIfNeeded}
            autoComplete={'off'}
            autoFocus
        />;
    } else if (type === ECustomPropertyType.NUMBER) {
        input = <Input
            className={isChanged ? classWithSave : ''}
            state={isInputError ? 'error' : null}
            autosize={false}
            readOnly={isReadonly}
            type={'number'}
            value={castValue(value) !== null ? String(value) : ''}
            onBlur={onBlurInput}
            onFocus={onFocus}
            onChange={onChangeInput}
            onKeyDown={onKeyDown}
            onClick={setCardHeightIfNeeded}
            autoComplete={'off'}
            autoFocus
        />;
    } else if (type === ECustomPropertyType.DATE) {
        const valueNum = value ? Number(value) : null;
        input = <DatepickerHOC
            isCustomProperty
            isSingle
            isReadonly={isReadonly}
            selectedFromDate={valueNum && !isNaN(valueNum) ? new Date(valueNum) : null}
            onChange={onChangeDate}
            onKeyDown={(e) => onKeyDown(e)}
            onBlur={onBlurInput}
            onFocus={onFocus}
            portalId={ID_ASIDE_PANEL_DATEPICKER_PORTAL}
            onCalendarOpened={() => setFocus(true)}
            onCalendarClosed={() => setFocus(false)}
            updateInputValue={updateInputValue}
        />;
    } else if (type === ECustomPropertyType.EMAIL) {
        input = <Input
            className={isChanged ? classWithSave : ''}
            autosize={false}
            readOnly={isReadonly}
            state={error || isInputError ? 'error' : null}
            type={'email'}
            value={castValue(value) !== null ? String(value) : ''}
            onBlur={onBlurInput}
            onFocus={onFocus}
            onChange={onChangeInput}
            onKeyDown={onKeyDown}
            onClick={setCardHeightIfNeeded}
            autoComplete={'off'}
            autoFocus
        />;
    } else if (type === ECustomPropertyType.PHONE) {
        input = <Input
            className={isChanged ? classWithSave : ''}
            autosize={false}
            readOnly={isReadonly}
            state={error || isInputError ? 'error' : null}
            type={'tel'}
            value={castValue(value) !== null ? String(value) : ''}
            onBlur={onBlurInput}
            onFocus={onFocus}
            onChange={onChangeInput}
            onKeyDown={onKeyDown}
            onClick={setCardHeightIfNeeded}
            autoComplete={'off'}
            autoFocus
        />
    } else if (type === ECustomPropertyType.SELECT) {
        let active: number = CUSTOM_PROPERTY_SELECT_DEFAULT_VALUE === value ? 0 : null;
        const items = !options ? null : [
            {
                text: 'None',
                value: CUSTOM_PROPERTY_SELECT_DEFAULT_VALUE,
            },
            ...options.map((option, index) => {
                if (value && value === option.id) active = index + 1;
                return {
                    text: option.value,
                    value: option.id,
                };
            })
        ];
        input = <>
            <Select
                active={active}
                className={isError ? classInput + '--error' : ''}
                dropdownClassName={className + '-dropdown'}
                isFitWindow={isPopup}
                state={isInputError ? 'error' : null}
                options={items}
                readOnly={isReadonly || !options || !options.length}
                onChange={onChangeSelect}
                onKeyDown={onKeyDown}
                onBlur={onBlurInput}
                onFocus={onFocus}
                onOpen={() => setFocus(true)}
                onClose={() => setFocus(false)}
                onClick={setCardHeightIfNeeded}
            />
            {(!options || !options.length) &&
                <Button
                    className={className + '-option-add'}
                    variant={'add'}
                    onClick={onEdit}
                >
                    Add option
                </Button>
            }
        </>
    }

    let placeholder;
    if (!value && inputValue && type === ECustomPropertyType.DATE) {
        placeholder = '';
    } else {
        placeholder = `${!value ? CUSTOM_LABEL_MAP[type] : ''}${name}${type === ECustomPropertyType.SELECT && !value ? '...' : ''}`
    }

    if (removed) return null;

    return <div
        className={`
            ${className}
            ${className}--${id}
            ${className}--${type.toLowerCase()}
            ${!value ? className + '--empty' : ''}
            ${error ? className + '--error' : ''}
            ${isReadonly ? className + '--readonly' : ''}
            ${isRequired ? className + '--required' : ''}
            ${isDatePushed && !isKanbanCard ? className + '--date-pushed' : ''}
            ${isKanbanCard ? CARD_CONTROL_CLASS : ''}
            ${isKanbanCard ? className + '--kanban-card' : ''}
        `}
    >
        <div className={classInput + '-wrapper'} onClick={onShowBoardLimitSnackbarIfNeeded}>
            {isEdit || isError || type === ECustomPropertyType.SELECT || type === ECustomPropertyType.DATE ?
                input
                : <div
                    className={classInput + '-html'}
                    role={'button'}
                    tabIndex={0}
                    ref={ref}
                    aria-label={value}
                    onClick={onClickHtml}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            onStartEdit();
                        }
                    }}
                >
                    {ReactHtmlParser(markdown)}
                </div>
            }
        </div>
        {isShowCloseButton ?
            <Button
                className={className + '-close-input-button'}
                variant={'icon'}
            >
                <Icon xlink={'close'} size={24} onClick={onCancelEdit} />
            </Button> :
            <CustomPropertyTypeSelect cardId={cardId} type={type} />
        }
        {(!isFocus || !isEdit) && <Tooltip
            {...getAsidePanelTooltip({
                className: `${className}-label-tooltip ${isKanbanCard ? className + '-label-tooltip--kanban-card' : ''}`,
                direction: 'up-right',
                value: escape(name),
            })}
        >
            <span className={className + '-label'}>
                {placeholder}
            </span>
        </Tooltip>}
        {type === ECustomPropertyType.DATE && value && !isKanbanCard && isDatePushed &&
            <CustomPropertiesPushToGoogleCalendarHOC cardId={cardId} id={id} />
        }
        {!isReadonly && !isKanbanCard && <CustomPropertyActionsHOC cardId={cardId} customPropertyId={id}/>}
        {error &&
            <ErrorHint
                arrow={'down'}
                direction={'up'}
                portalSelector={`.${className}--${id}`}
                selector={`.${className}--${id}`}
                value={error}
            />
        }
        {!isReadonly && type !== ECustomPropertyType.DATE && type !== ECustomPropertyType.SELECT && !error &&
            <Button
                className={`
                    ${classSave}
                    ${isChanged ? classSave + '--show' : ''}
                `}
                onClick={onSave}
            >
                {CUSTOM_BUTTON_SAVE}
            </Button>
        }
    </div>;
};
